/* La Caza Cali — Search panel + drawer nav uplift (Phase 4.3)

   Pure vanilla, no dependencies. Loaded with <script defer> from
   sections/header.liquid, after assets/lacaza-drawer.js (which still
   owns basic drawer open/close + sticky header shadow — untouched).

   Responsibilities:
     - Search panel open/close (header icon + drawer search pill),
       scrim/Esc close, focus trap, focus return, body scroll lock
     - /search/suggest.json fetch (debounced), results + collections
       render, popular-row swap, empty state, chips
     - Keyboard: arrow keys move through the visible tile/collection
       list, Enter opens the highlighted one
     - Drawer inline submenu expand (Studio / Care)
     - Drawer focus trap + focus return to the hamburger on close
       (lacaza-drawer.js only did this for the Escape path)
*/

(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapTab(container, event) {
    var items = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first || !container.contains(document.activeElement)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      try {
        var format = (window.theme && window.theme.moneyFormat) || window.Shopify.money_format;
        return window.Shopify.formatMoney(cents, format);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* =====================================================================
     Search panel
     ===================================================================== */

  var panel = document.querySelector('[data-search-panel]');
  if (panel) {
    var openers = document.querySelectorAll('[data-search-open]');
    var closers = panel.querySelectorAll('[data-search-close]');
    var sheet = panel.querySelector('.lacaza-search-panel__sheet');
    var input = panel.querySelector('[data-search-input]');
    var clearBtn = panel.querySelector('[data-search-clear]');
    var chipsWrap = panel.querySelector('[data-search-chips]');
    var popularSection = panel.querySelector('[data-search-popular]');
    var resultsSection = panel.querySelector('[data-search-results]');
    var resultsGrid = panel.querySelector('[data-search-results-grid]');
    var collectionsWrap = panel.querySelector('[data-search-collections]');
    var emptyMsg = panel.querySelector('[data-search-empty]');
    var seeAllLink = panel.querySelector('[data-search-seeall]');
    var seeAllTerm = panel.querySelector('[data-search-seeall-term]');
    var statusEl = panel.querySelector('[data-search-status]');
    var form = panel.querySelector('[data-search-form]');
    var body = document.body;

    var lastFocused = null;
    var debounceTimer = null;
    var abortController = null;
    var selectedIndex = -1;
    var selectableItems = [];

    function closeDrawerIfOpen() {
      var drawer = document.querySelector('[data-drawer]');
      var drawerToggle = document.querySelector('[data-drawer-toggle]');
      if (body.classList.contains('lacaza-drawer-open')) {
        body.classList.remove('lacaza-drawer-open');
        body.style.overflow = '';
        if (drawer) drawer.setAttribute('aria-hidden', 'true');
        if (drawerToggle) drawerToggle.setAttribute('aria-expanded', 'false');
        return drawerToggle;
      }
      return null;
    }

    function setSelectableItems(items) {
      selectableItems.forEach(function (el) { el.classList.remove('is-selected'); el.removeAttribute('aria-selected'); });
      selectableItems = items || [];
      selectedIndex = -1;
    }

    function moveSelection(delta) {
      if (!selectableItems.length) return;
      if (selectedIndex >= 0) {
        selectableItems[selectedIndex].classList.remove('is-selected');
        selectableItems[selectedIndex].removeAttribute('aria-selected');
      }
      selectedIndex += delta;
      if (selectedIndex < 0) selectedIndex = selectableItems.length - 1;
      if (selectedIndex >= selectableItems.length) selectedIndex = 0;
      var el = selectableItems[selectedIndex];
      el.classList.add('is-selected');
      el.setAttribute('aria-selected', 'true');
      el.scrollIntoView({ block: 'nearest' });
    }

    function openPanel(trigger) {
      // If the trigger was the drawer's search pill, the drawer (and the
      // pill along with it) is about to become visibility:hidden — that
      // makes the pill unfocusable, so return focus to the hamburger
      // instead of a dead element when the panel later closes.
      var closedDrawerToggle = closeDrawerIfOpen();
      lastFocused = closedDrawerToggle || trigger || document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      body.classList.add('lacaza-search-open');
      body.style.overflow = 'hidden';
      openers.forEach(function (el) { el.setAttribute('aria-expanded', 'true'); });
      requestAnimationFrame(function () { input.focus(); });
    }

    function closePanel() {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      body.classList.remove('lacaza-search-open');
      body.style.overflow = '';
      openers.forEach(function (el) { el.setAttribute('aria-expanded', 'false'); });
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    function isOpen() {
      return panel.classList.contains('is-open');
    }

    openers.forEach(function (opener) {
      opener.addEventListener('click', function (e) {
        e.preventDefault();
        if (isOpen()) {
          closePanel();
        } else {
          openPanel(opener);
        }
      });
    });

    closers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closePanel();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        closePanel();
        return;
      }
      if (e.key === 'Tab') {
        trapTab(sheet, e);
        return;
      }
      if (e.key === 'ArrowDown' && document.activeElement === input) {
        e.preventDefault();
        moveSelection(1);
        return;
      }
      if (e.key === 'ArrowUp' && document.activeElement === input) {
        e.preventDefault();
        moveSelection(-1);
        return;
      }
    });

    function showState(state) {
      // state: 'popular' | 'results' | 'empty'
      popularSection.hidden = state !== 'popular';
      resultsSection.hidden = state !== 'results';
      emptyMsg.hidden = state !== 'empty';
      seeAllLink.hidden = state === 'popular';
    }

    function clearResults() {
      resultsGrid.innerHTML = '';
      collectionsWrap.innerHTML = '';
    }

    function buildProductTile(product) {
      var a = document.createElement('a');
      a.className = 'lacaza-search-tile';
      a.href = product.url;

      var media = document.createElement('span');
      media.className = 'lacaza-search-tile__media';
      var imgUrl = (product.featured_image && product.featured_image.url) || product.image;
      if (imgUrl) {
        var img = document.createElement('img');
        img.src = imgUrl;
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
      }
      a.appendChild(media);

      var title = document.createElement('span');
      title.className = 'lacaza-search-tile__title';
      title.textContent = product.title || '';
      a.appendChild(title);

      var price = document.createElement('span');
      price.className = 'lacaza-search-tile__price';
      var cents = Math.round(parseFloat(product.price || 0) * 100);
      price.textContent = formatMoney(cents);
      a.appendChild(price);

      return a;
    }

    function buildCollectionLink(collection) {
      var a = document.createElement('a');
      a.className = 'lacaza-search-collection';
      a.href = collection.url;
      a.textContent = collection.title || '';
      return a;
    }

    function renderResults(products, collections, term) {
      clearResults();

      if (!products.length && !collections.length) {
        showState('empty');
        seeAllLink.hidden = false;
        seeAllLink.href = '/search?q=' + encodeURIComponent(term);
        if (seeAllTerm) seeAllTerm.textContent = term;
        setSelectableItems([]);
        if (statusEl) statusEl.textContent = 'No results for ' + term;
        return;
      }

      var items = [];
      products.slice(0, 6).forEach(function (p) {
        var tile = buildProductTile(p);
        resultsGrid.appendChild(tile);
        items.push(tile);
      });

      if (collections.length) {
        var label = document.createElement('p');
        label.className = 'lacaza-search-panel__collections-label';
        label.textContent = 'Collections';
        collectionsWrap.appendChild(label);
        collections.slice(0, 4).forEach(function (c) {
          var link = buildCollectionLink(c);
          collectionsWrap.appendChild(link);
          items.push(link);
        });
      }

      showState('results');
      seeAllLink.hidden = false;
      seeAllLink.href = '/search?q=' + encodeURIComponent(term);
      if (seeAllTerm) seeAllTerm.textContent = term;
      setSelectableItems(items);
      if (statusEl) statusEl.textContent = products.length + ' results for ' + term;
    }

    function performSearch(term) {
      if (abortController) abortController.abort();
      abortController = new AbortController();

      fetch('/search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product,collection&resources[limit]=6', {
        signal: abortController.signal
      })
        .then(function (res) { return res.json(); })
        .then(function (json) {
          var results = (json.resources && json.resources.results) || {};
          renderResults(results.products || [], results.collections || [], term);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
        });
    }

    function handleQuery(term) {
      term = (term || '').trim();
      if (term.length < 2) {
        showState('popular');
        setSelectableItems([]);
        clearBtn.hidden = term.length === 0;
        return;
      }
      clearBtn.hidden = false;
      performSearch(term);
    }

    if (input) {
      input.addEventListener('input', function () {
        clearBtn.hidden = input.value.length === 0;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () { handleQuery(input.value); }, 180);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        showState('popular');
        setSelectableItems([]);
        input.focus();
      });
    }

    if (chipsWrap) {
      chipsWrap.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-search-chip]');
        if (!chip) return;
        var term = chip.getAttribute('data-search-chip');
        input.value = term;
        clearBtn.hidden = false;
        input.focus();
        clearTimeout(debounceTimer);
        handleQuery(term);
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        if (selectedIndex >= 0 && selectableItems[selectedIndex]) {
          e.preventDefault();
          window.location.href = selectableItems[selectedIndex].href;
        }
        // else: default submit navigates to /search?q=... (no-JS-equivalent fallback)
      });
    }
  }

  /* =====================================================================
     Drawer: inline submenu expand (Studio / Care) + focus polish
     ===================================================================== */

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-drawer-submenu-toggle]');
    if (toggle) {
      var panelId = toggle.getAttribute('aria-controls');
      var subPanel = panelId && document.getElementById(panelId);
      var isOpenNow = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', isOpenNow ? 'false' : 'true');
      if (subPanel) subPanel.classList.toggle('is-open', !isOpenNow);
      return;
    }

    // Return focus to the hamburger when the drawer closes via scrim/close
    // button (lacaza-drawer.js only restores focus on the Escape path).
    if (e.target.closest('[data-drawer-close]')) {
      var hamburger = document.querySelector('[data-drawer-toggle]');
      if (hamburger) {
        requestAnimationFrame(function () { hamburger.focus(); });
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var body = document.body;
    if (!body.classList.contains('lacaza-drawer-open')) return;
    var drawerPanel = document.querySelector('.lacaza-drawer__panel');
    if (drawerPanel) trapTab(drawerPanel, e);
  });
})();
